import { isAbove, isBelow } from "./comparisons";

const UP = 1;
const DOWN = -1;

/**
 * One step of the Parabolic SAR exactly as TV Std.sar: the second bar
 * picks the trend by its close, then the SAR moves towards the extreme
 * point by the acceleration factor, which grows by `increment` with every
 * new extreme up to `maximum`; crossing the price flips the trend.
 * The state of the bar is written to `state` at `index` (trend, factor,
 * extreme, sar), so a tick can calculate the last bar again from the one before.
 *
 * @param {object[]} bars
 * @param {number} index
 * @param {{ start: number, increment: number, maximum: number }} settings
 * @param {{ trend: number[], factor: number[], extreme: number[], sar: number[] }} state
 * @returns {number} the SAR of the bar, NaN on the first bar
 */
export const parabolicSarAt = (bars, index, { start, increment, maximum }, state) => {
  const bar = bars[index];
  const previous = bars[index - 1];
  let trend = state.trend[index - 1];
  let factor = state.factor[index - 1];
  let extreme = state.extreme[index - 1];
  let sar = NaN;

  if (index === 1) {
    trend = isAbove(bar.close, previous.close) ? UP : DOWN;
    extreme = trend === UP ? bar.high : bar.low;
    factor = start;
  }

  if (index >= 1) {
    const previousSar = index === 1 ? (trend === UP ? previous.low : previous.high) : state.sar[index - 1];
    let isReversed = index === 1;

    sar = previousSar + factor * (extreme - previousSar);

    if (trend === UP && isAbove(sar, bar.low)) {
      isReversed = true;
      trend = DOWN;
      sar = Math.max(bar.high, extreme);
      extreme = bar.low;
      factor = start;
    } else if (trend === DOWN && isBelow(sar, bar.high)) {
      isReversed = true;
      trend = UP;
      sar = Math.min(bar.low, extreme);
      extreme = bar.high;
      factor = start;
    }

    if (!isReversed && trend === UP && isAbove(bar.high, extreme)) {
      extreme = bar.high;
      factor = Math.min(factor + increment, maximum);
    } else if (!isReversed && trend === DOWN && isBelow(bar.low, extreme)) {
      extreme = bar.low;
      factor = Math.min(factor + increment, maximum);
    }

    // The SAR never goes into the range of the two previous bars
    const beforePrevious = bars[index - 2];

    if (trend === UP) {
      sar = Math.min(sar, previous.low, beforePrevious ? beforePrevious.low : Infinity);
    } else {
      sar = Math.max(sar, previous.high, beforePrevious ? beforePrevious.high : -Infinity);
    }
  }

  state.trend[index] = trend;
  state.factor[index] = factor;
  state.extreme[index] = extreme;
  state.sar[index] = sar;

  return sar;
};
